import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { OnboardingFlow, Tier } from '@mondaygpt/constants'
import { Offering, Package } from '@revenuecat/purchases-js'
import { Button, Switch } from 'components'
import { useAnalytics } from 'hooks'
import { cn } from 'lib/utils'
import { ChevronDown, Gift, Loader2, Ticket } from 'lucide-react'
import { selectBusinessId } from 'modules/authentication'
import {
  applyPromoOffering,
  BillingPeriod,
  clearPromoOffering,
  purchasePackage,
  selectAppliedPromo,
  selectPromoOffering,
  selectRevenueCat,
  selectShowUpgrade,
  type AppliedPromo,
} from 'modules/billing'
import { fetchBusinessBilling } from 'modules/billing/slices/credit-balance.slice'
import { getCheckoutProperties } from 'modules/billing/utils/checkout-properties'
import {
  formatBaseMonthlyPrice,
  formatBaseYearlyTotal,
  formatMonthlyPrice,
  formatYearlyTotal,
  hasFreeTrial,
  hasIntroDiscount,
  getPackageForPlan,
  offeringHasFreeTrial,
  PLANS_CONFIG,
  type PlanConfig,
} from 'modules/billing/utils/plan-pricing'
import { intercom } from 'services/intercom'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { BillingService } from 'openapi'
import { AnimatePresence, motion } from 'framer-motion'
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import { clearPendingPromoCode, selectPendingPromoCode } from '../slices/onboarding-view.slice'
import { Eyebrow } from './Eyebrow'
import { MobileStickyFooter } from './MobileStickyFooter'
import { SPRITE_CHECK } from './OnboardingShell'

const PAYWALL_SURFACE = 'onboarding' as const
const PAYWALL_SOURCE = 'onboarding' as const
const TRIAL_DURATION_DAYS = 7
const TRIAL_REMINDER_DAY = 5

type Screen = 'trial-info' | 'plan-picker'

/**
 * Trial-info personalization, derived in DarkOnboardingFlow from the
 * already-staged brand kit + presence data. The trial-info features list
 * uses these values to swap generic copy for "Posts in {Patagonia}'s voice,
 * auto-published to {Instagram and Facebook}, built for {grow audience}…"
 * Optional: unset → fall back to generic copy.
 */
export interface TrialPersonalization {
  businessName: string
  channelLabels: string[]
  goalLabels: string[]
}

interface DarkPaywallStepProps {
  flowId: OnboardingFlow
  onNext: () => void
  personalization?: TrialPersonalization
  /**
   * Disable the auto-skip for already-subscribed users. Set by the
   * add-another-business flow so the new business gets its own paywall
   * pass, since Subscription is 1:1 with businessId.
   */
  forceShow?: boolean
}

/**
 * Paywall step on the new dark/acid design system.
 *
 * Self-contained: owns the trial-info → plan-picker mini-flow, the RevenueCat
 * config + offering, the tester-grant short-circuit, the purchase dispatch,
 * and the analytics. The two visual surfaces (timeline + plan cards) are
 * inlined so the whole paywall lives in one file and migrates atomically.
 *
 * Phase 3 already saved the brand kit, so we skip straight to `onNext` once
 * a plan is purchased — no `onPaymentSuccess` callback needed.
 */
export const DarkPaywallStep: React.FC<DarkPaywallStepProps> = ({
  flowId,
  onNext,
  personalization,
  forceShow = false,
}) => {
  const [currentScreen, setCurrentScreen] = useState<Screen>('trial-info')
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { track } = useAnalytics()
  const dispatch = useAppDispatch()
  const hasSkipped = useRef(false)

  const {
    currentOffering,
    error: revenueCatError,
    isConfigured,
    isLoading,
  } = useAppSelector(selectRevenueCat)
  const showUpgrade = useAppSelector(selectShowUpgrade)
  const businessId = useAppSelector(selectBusinessId)
  const promoOffering = useAppSelector(selectPromoOffering)
  const appliedPromo = useAppSelector(selectAppliedPromo)
  const pendingPromoCode = useAppSelector(selectPendingPromoCode)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [isApplyingPromo, setIsApplyingPromo] = useState(false)
  const hasAutoAppliedPromo = useRef(false)

  // RevenueCat evaluates trial/intro eligibility per customer and strips
  // ineligible pricing phases from the offering server-side. Everything the
  // paywall claims (trial, promo discount) must derive from the phases that
  // actually survived, or the copy lies to returning customers.
  const activeOffering = promoOffering ?? currentOffering
  const offeringHasTrial = offeringHasFreeTrial(activeOffering)
  // `some` (not `every`): a partially stripped offering still means at least
  // one card silently shows full price next to an "applied" code banner.
  const promoPackages = promoOffering?.availablePackages ?? []
  const promoStripped = Boolean(
    appliedPromo && promoPackages.length > 0 && promoPackages.some(pkg => !hasIntroDiscount(pkg))
  )

  // A customer with no trial phase must never see the "free for 7 days"
  // pitch. Derived at render (not an effect) so the timeline never mounts —
  // no wrong-screen flash and no stray TRIAL_TIMELINE_VIEWED event.
  const effectiveScreen: Screen = offeringHasTrial ? currentScreen : 'plan-picker'

  const trackedStrippedCode = useRef<string | null>(null)
  useEffect(() => {
    if (!promoStripped || !appliedPromo) return
    if (trackedStrippedCode.current === appliedPromo.code) return
    trackedStrippedCode.current = appliedPromo.code
    track(AnalyticsEvents.Paywall.PROMO_CODE_FAILED, {
      surface: PAYWALL_SURFACE,
      source: PAYWALL_SOURCE,
      promo_code: appliedPromo.code,
      error_reason: 'intro_price_stripped',
    })
  }, [promoStripped, appliedPromo, track])

  // Already-subscribed users skip the paywall — except in add-another-business
  // mode (`forceShow`), where the new business needs its own subscription
  // regardless of any existing one.
  useEffect(() => {
    if (!isConfigured) return
    if (forceShow) return
    if (!showUpgrade && !hasSkipped.current) {
      hasSkipped.current = true
      track(AnalyticsEvents.Paywall.PAYWALL_SKIPPED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        reason: 'existing_subscription',
      })
      onNext()
    }
  }, [isConfigured, showUpgrade, onNext, track, forceShow])

  useEffect(() => {
    if (isConfigured && showUpgrade) {
      track(AnalyticsEvents.Paywall.PAYWALL_VIEWED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        screen: effectiveScreen,
        flow_id: flowId,
      })
    }
  }, [isConfigured, showUpgrade, track, flowId, effectiveScreen])

  const handleApplyPromoCode = async (rawCode: string, autoApplied = false): Promise<void> => {
    const code = rawCode.trim().toUpperCase()
    if (!code || isApplyingPromo) return

    if (!/^[A-Z0-9][A-Z0-9_-]{2,31}$/.test(code)) {
      setPromoError(PROMO_ERROR_COPY.invalid_format)
      track(AnalyticsEvents.Paywall.PROMO_CODE_FAILED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        promo_code: code,
        error_reason: 'invalid_format',
      })
      return
    }

    setIsApplyingPromo(true)
    setPromoError(null)

    try {
      const result = await BillingService.promoCodeControllerRedeemPromoCodeV1({ code })

      if (result.status === 'granted') {
        track(AnalyticsEvents.Paywall.PROMO_CODE_REDEEMED, {
          surface: PAYWALL_SURFACE,
          source: PAYWALL_SOURCE,
          promo_code: result.code,
          campaign: result.campaign,
          tier: result.tier,
          duration: result.duration,
          auto_applied: autoApplied,
        })
        if (businessId) dispatch(fetchBusinessBilling(businessId))
        dispatch(clearPendingPromoCode())
        onNext()
        return
      }

      // Discount code: swap the paywall to the partner offering.
      if (result.rcOfferingId) {
        await dispatch(
          applyPromoOffering({
            offeringId: result.rcOfferingId,
            code: result.code,
            campaign: result.campaign,
          })
        ).unwrap()
        track(AnalyticsEvents.Paywall.PROMO_CODE_APPLIED, {
          surface: PAYWALL_SURFACE,
          source: PAYWALL_SOURCE,
          promo_code: result.code,
          campaign: result.campaign,
          rc_offering_id: result.rcOfferingId,
          auto_applied: autoApplied,
        })
        dispatch(clearPendingPromoCode())
      }
    } catch (error) {
      const reason = getPromoErrorReason(error)
      setPromoError(PROMO_ERROR_COPY[reason] ?? PROMO_ERROR_COPY.network)
      track(AnalyticsEvents.Paywall.PROMO_CODE_FAILED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        promo_code: code,
        error_reason: reason,
      })
      console.warn('[DarkPaywallStep] promo code apply failed:', reason)
    } finally {
      setIsApplyingPromo(false)
    }
  }

  const handleRemovePromoCode = () => {
    if (!appliedPromo) return
    track(AnalyticsEvents.Paywall.PROMO_CODE_REMOVED, {
      surface: PAYWALL_SURFACE,
      source: PAYWALL_SOURCE,
      promo_code: appliedPromo.code,
      campaign: appliedPromo.campaign,
    })
    dispatch(clearPromoOffering())
    setPromoError(null)
  }

  // Auto-apply a ?promo=CODE carried through signup, once the paywall is
  // actually showing (ref-deduped against StrictMode double-runs).
  useEffect(() => {
    if (!isConfigured || !showUpgrade) return
    if (!pendingPromoCode || hasAutoAppliedPromo.current) return
    hasAutoAppliedPromo.current = true
    handleApplyPromoCode(pendingPromoCode, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigured, showUpgrade, pendingPromoCode])

  const handleSelectPlan = async (pkg: Package | null, tier: Tier, period: BillingPeriod) => {
    const checkoutProps = getCheckoutProperties(pkg, tier, period)

    track(AnalyticsEvents.Paywall.PAYWALL_UPGRADE_CTA_CLICKED, {
      surface: PAYWALL_SURFACE,
      source: PAYWALL_SOURCE,
      ...checkoutProps,
    })

    if (!pkg) {
      setErrorMessage('This plan is not available yet. Try yearly.')
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      const vendorId = pkg.webBillingProduct?.identifier
      if (vendorId) {
        const granted = await checkTesterGrant(vendorId)
        if (granted) {
          if (businessId) dispatch(fetchBusinessBilling(businessId))
          track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
            surface: PAYWALL_SURFACE,
            source: PAYWALL_SOURCE,
            reason: 'tester_grant',
            ...checkoutProps,
          })
          track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
            surface: PAYWALL_SURFACE,
            source: PAYWALL_SOURCE,
            skipped: true,
            reason: 'tester_grant',
            ...checkoutProps,
          })
          onNext()
          setIsProcessing(false)
          return
        }
      }
    } catch (err) {
      console.warn('[DarkPaywallStep] Tester grant check failed, proceeding with payment:', err)
    }

    try {
      track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        ...(appliedPromo ? { promo_code: appliedPromo.code } : {}),
        ...checkoutProps,
      })

      const result = await dispatch(purchasePackage({ packageToPurchase: pkg }))

      if (result.meta.requestStatus === 'rejected') {
        if (result.payload === '__cancelled__') {
          setIsProcessing(false)
          return
        }
        throw new Error((result.payload as string) || 'Purchase failed')
      }

      if (businessId) dispatch(fetchBusinessBilling(businessId))

      track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        ...(appliedPromo ? { promo_code: appliedPromo.code } : {}),
        ...checkoutProps,
      })

      onNext()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Purchase failed'
      setErrorMessage(message)
      console.error('[DarkPaywallStep] checkout failed:', { tier, period, error })

      track(AnalyticsEvents.Paywall.CHECKOUT_FAILED, {
        surface: PAYWALL_SURFACE,
        source: PAYWALL_SOURCE,
        plan_name: checkoutProps.plan_name,
        billing_period: checkoutProps.billing_period,
        error_message: message,
      })
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading && !isConfigured) {
    return (
      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-3 text-center">
        <Loader2 className="h-7 w-7 animate-spin text-acid" strokeWidth={1.8} />
        <p className="text-[14px] text-paper-dim">Loading plans…</p>
      </div>
    )
  }

  if (effectiveScreen === 'trial-info') {
    return (
      <DarkTrialTimeline
        onStartTrial={() => setCurrentScreen('plan-picker')}
        personalization={personalization}
      />
    )
  }

  return (
    <DarkPlanPicker
      onRemovePromoCode={handleRemovePromoCode}
      offering={activeOffering}
      onSelectPlan={handleSelectPlan}
      isProcessing={isProcessing}
      errorMessage={errorMessage || revenueCatError}
      appliedPromo={appliedPromo}
      promoStripped={promoStripped}
      promoError={promoError}
      isApplyingPromo={isApplyingPromo}
      onApplyPromoCode={handleApplyPromoCode}
      initialPromoCode={pendingPromoCode}
    />
  )
}

// ─── trial-info screen ──────────────────────────────────────────────────────

interface DarkTrialTimelineProps {
  onStartTrial: () => void
  personalization?: TrialPersonalization
}

const DarkTrialTimeline: React.FC<DarkTrialTimelineProps> = ({ onStartTrial, personalization }) => {
  const { track } = useAnalytics()

  useEffect(() => {
    track(AnalyticsEvents.Paywall.TRIAL_TIMELINE_VIEWED, {
      surface: PAYWALL_SURFACE,
      source: PAYWALL_SOURCE,
    })
  }, [track])

  const { reminderDate, chargeDate } = useMemo(() => {
    const reminder = new Date()
    reminder.setDate(reminder.getDate() + TRIAL_REMINDER_DAY)
    const charge = new Date()
    charge.setDate(charge.getDate() + TRIAL_DURATION_DAYS)
    return { reminderDate: reminder, chargeDate: charge }
  }, [])

  const handleStart = () => {
    track(AnalyticsEvents.Paywall.TRIAL_CHECKOUT_CTA_CLICKED, {
      surface: PAYWALL_SURFACE,
      source: PAYWALL_SOURCE,
    })
    onStartTrial()
  }

  const features = buildTrialFeatures(personalization)

  return (
    <div className="mx-auto flex max-w-[640px] flex-col gap-7">
      <div className="flex flex-col gap-3 px-1">
        <Eyebrow>7-day trial</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          Get Taka free for <span className="text-acid">{TRIAL_DURATION_DAYS} days</span>.
        </h2>
        <p className="lede max-w-[520px] !text-[15px] !leading-[1.55]">
          Cancel before {formatDate(chargeDate)} and pay $0. We'll remind you on{' '}
          <strong className="font-semibold text-paper">{formatDate(reminderDate)}</strong>.
        </p>
      </div>

      <div className="card flex flex-col gap-7 p-6 sm:p-8">
        <TrialTrack />

        <div className="border-t border-line pt-6">
          <ul className="flex flex-col gap-3">
            {features.map((f, i) => (
              <Feature key={i} title={f.title} desc={f.desc} />
            ))}
          </ul>
        </div>
      </div>

      <MobileStickyFooter>
        <Button
          variant="t3-primary"
          size="t3-lg"
          onClick={handleStart}
          className="!max-w-[420px]"
          data-testid="trial-start"
        >
          Start my free trial →
        </Button>
        <p className="text-center text-[12px] text-paper-faint">
          No charge today. Cancel any time in one click.
        </p>
      </MobileStickyFooter>
    </div>
  )
}

interface FeatureRow {
  title: string
  desc: React.ReactNode
}

const GENERIC_FEATURES: FeatureRow[] = [
  {
    title: 'Hours back every week',
    desc: 'A week of on-brand posts to all your channels. Ready in minutes.',
  },
  {
    title: 'Always sounds like you',
    desc: 'Trained on your voice. Every post, every channel.',
  },
  {
    title: 'Pointed at what matters',
    desc: 'Built around your goals. Not generic templates.',
  },
]

/**
 * Pain → Promise → Proof. Title is the universal benefit, description carries
 * the specific personalization as evidence we already know the user.
 */
function buildTrialFeatures(p: TrialPersonalization | undefined): FeatureRow[] {
  if (!p) return GENERIC_FEATURES
  const channels = labelOr(p.channelLabels, 'all your channels')
  const goals = labelOr(p.goalLabels, 'your goals')
  return [
    {
      title: 'Hours back every week',
      desc: (
        <>
          A week of on-brand posts to <Highlight>{channels}</Highlight>. Ready in minutes.
        </>
      ),
    },
    {
      title: 'Always sounds like you',
      desc: (
        <>
          Trained on <Highlight>{p.businessName}</Highlight>'s voice. Every post, every channel.
        </>
      ),
    },
    {
      title: 'Pointed at what matters',
      desc: (
        <>
          Built around <Highlight>{goals}</Highlight>. Not generic templates.
        </>
      ),
    },
  ]
}

const Highlight: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-medium text-paper">{children}</span>
)

const labelOr = (xs: string[], fallback: string): string => {
  if (xs.length === 0) return fallback
  if (xs.length === 1) return xs[0]
  if (xs.length === 2) return `${xs[0]} and ${xs[1]}`
  return `${xs.slice(0, -1).join(', ')}, and ${xs[xs.length - 1]}`
}

const TrialTrack: React.FC = () => (
  <>
    {/* Mobile: vertical timeline. Bar runs top-to-bottom with the three
        milestone dots threaded along it; copy sits to the right of each
        dot. Horizontal bar from the desktop variant doesn't read on
        narrow viewports — the vertical layout matches how a phone is
        held and lets the trio breathe. */}
    <div className="sm:hidden">
      <VerticalTrialTrack />
    </div>

    {/* Desktop: horizontal bar with absolute-positioned milestones below. */}
    <div className="hidden flex-col gap-5 sm:flex">
      <div className="relative h-4">
        {/* overflow-hidden so the ripple stays inside the bar; dots sit
            outside (z-1) so their glow rings can extend past the bar's edges. */}
        <div
          className="absolute inset-0 overflow-hidden rounded-full border border-line bg-bg-3"
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
        >
          <span className="trial-pulse" aria-hidden />
          <div
            className="absolute inset-y-0 right-0"
            style={{
              width: '46%',
              background:
                'linear-gradient(to right, transparent, color-mix(in srgb, var(--acid) 12%, transparent) 55%, var(--acid-tint) 100%)',
            }}
          />
        </div>
        {Array.from({ length: TRIAL_DURATION_DAYS }, (_, i) => (
          <span
            key={i}
            className="trial-dot"
            data-tone={dotToneForIndex(i)}
            style={{ left: `${dotPercent(i)}%` }}
          />
        ))}
      </div>

      <div className="relative h-12 text-[12px] text-paper-dim">
        {TRIAL_MILESTONES.map(m => (
          <Milestone key={m.eyebrow} {...m} />
        ))}
      </div>
    </div>
  </>
)

/** The day-index → dot tone mapping that both bars share. */
const dotToneForIndex = (i: number): 'today' | 'reminder' | 'charge' | 'past' => {
  if (i === TRIAL_DURATION_DAYS - 1) return 'charge'
  if (i === 0) return 'today'
  if (i === TRIAL_REMINDER_DAY - 1) return 'reminder'
  return 'past'
}

/** Mobile-only vertical timeline. Same primitives as the desktop bar
    (pill bar, blurred acid pulse, end-anchored gradient overlay, dots
    with their tone treatments) just rotated 90° and trimmed for the
    space budget — past day-dots are dropped (decorative noise on a
    phone), only the 3 named milestones render, distributed evenly
    along a shorter bar so the card stays tight. */
const VerticalTrialTrack: React.FC = () => {
  // Distribute the 3 named milestones evenly with a hair of breathing
  // room at top/bottom so dots don't kiss the bar's rounded ends.
  const VERTICAL_DOT_TOPS = ['6%', '50%', '94%'] as const
  // Anchors: first milestone hangs below its dot, last hangs above,
  // middle centers — keeps labels from clipping or overlapping at the
  // chosen bar height without forcing pixel-padding hacks.
  const ANCHORS = ['top', 'center', 'bottom'] as const

  return (
    <div className="relative h-[200px] pl-10">
      {/* The bar itself — a vertical pill matching the desktop bar's
          treatment (bg-bg-3, border, inset highlight). Dots and pulse
          live inside its overflow-hidden inner; the dots' glow rings
          extend outside via z-1 (same trick as the horizontal variant). */}
      <div className="absolute top-0 bottom-0 left-0 w-3">
        <div
          className="absolute inset-0 overflow-hidden rounded-full border border-line bg-bg-3"
          style={{ boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.04)' }}
        >
          <span className="trial-pulse" data-orientation="vertical" aria-hidden />
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              height: '52%',
              background:
                'linear-gradient(to bottom, transparent, color-mix(in srgb, var(--acid) 14%, transparent) 50%, var(--acid-tint) 100%)',
            }}
          />
        </div>
        {TRIAL_MILESTONES.map((m, i) => (
          <span
            key={m.eyebrow}
            className="trial-dot"
            data-orientation="vertical"
            data-tone={dotToneForIndex(m.dotIndex)}
            style={{ top: VERTICAL_DOT_TOPS[i] }}
          />
        ))}
      </div>

      {TRIAL_MILESTONES.map((m, i) => (
        <VerticalMilestone key={m.eyebrow} {...m} top={VERTICAL_DOT_TOPS[i]} anchor={ANCHORS[i]} />
      ))}
    </div>
  )
}

const VerticalMilestone: React.FC<
  MilestoneContent & { top: string; anchor: 'top' | 'center' | 'bottom' }
> = ({ tone, eyebrow, title, sub, top, anchor }) => {
  const transform =
    anchor === 'top' ? undefined : anchor === 'center' ? 'translateY(-50%)' : 'translateY(-100%)'
  return (
    <div className="absolute right-0 left-10 flex flex-col gap-0.5" style={{ top, transform }}>
      <span
        className={cn(
          'mono text-[10px] tracking-[0.04em] uppercase',
          tone === 'acid' ? 'text-acid' : 'text-paper'
        )}
      >
        {eyebrow}
      </span>
      <span className="text-[14px] leading-[1.2] font-semibold text-paper">{title}</span>
      {sub && <span className="text-[11.5px] leading-[1.4] text-paper-faint">{sub}</span>}
    </div>
  )
}

interface MilestoneContent {
  /** Eyebrow text tone — 'acid' lifts the day chip on the charge milestone. */
  tone: 'paper' | 'acid'
  eyebrow: string
  title: string
  sub?: string
}

// Position on the desktop horizontal bar (label slightly inset from the
// dot so the label doesn't fall off the right edge). Vertical layout
// derives its position from `dotIndex` instead so the label can sit
// centered on the dot.
interface MilestonePosition {
  /** 0-indexed day in the trial (matches the dot index along the bar). */
  dotIndex: number
  /** Desktop label `left` — slightly less than the dot's actual position
      on the trailing milestone so wide labels don't clip the right edge. */
  desktopLeft: string
}

// Single source of truth — both renderers consume this so mobile and
// desktop copy can't drift.
const TRIAL_MILESTONES: Array<MilestoneContent & MilestonePosition> = [
  {
    dotIndex: 0,
    desktopLeft: '6%',
    tone: 'paper',
    eyebrow: 'Today',
    title: 'Start creating',
    sub: 'Pay nothing',
  },
  {
    dotIndex: TRIAL_REMINDER_DAY - 1,
    desktopLeft: '58%',
    tone: 'paper',
    eyebrow: `Day ${TRIAL_REMINDER_DAY}`,
    title: 'Reminder email',
  },
  {
    dotIndex: TRIAL_DURATION_DAYS - 1,
    desktopLeft: '78%',
    tone: 'acid',
    eyebrow: `Day ${TRIAL_DURATION_DAYS}`,
    title: 'Subscription begins',
    sub: 'Or cancel in one click',
  },
]

/** Day-index → percentage along the bar. Used for dot positions on both
    the horizontal (left%) and vertical (top%) variants, plus to align
    milestone labels with their named dots. */
const dotPercent = (dotIndex: number) => 6 + dotIndex * 13

const Milestone: React.FC<MilestoneContent & MilestonePosition> = ({
  desktopLeft,
  tone,
  eyebrow,
  title,
  sub,
}) => (
  <div className="absolute flex flex-col" style={{ left: desktopLeft }}>
    <span
      className={cn(
        'mono text-[10px] tracking-[0.04em] uppercase',
        tone === 'acid' ? 'text-acid' : 'text-paper'
      )}
    >
      {eyebrow}
    </span>
    <span className="mt-0.5 text-[13px] font-semibold whitespace-nowrap text-paper">{title}</span>
    {sub && <span className="text-[11px] whitespace-nowrap text-paper-faint">{sub}</span>}
  </div>
)

const Feature: React.FC<{ title: string; desc: React.ReactNode }> = ({ title, desc }) => (
  <li className="flex items-start gap-3">
    <span className="check-pill mt-0.5">
      <svg aria-hidden>
        <use href={SPRITE_CHECK} />
      </svg>
    </span>
    <div className="flex flex-col">
      <span className="text-[14px] font-medium text-paper">{title}</span>
      <span className="text-[13px] text-paper-dim">{desc}</span>
    </div>
  </li>
)

// ─── plan-picker screen ─────────────────────────────────────────────────────

interface DarkPlanPickerProps {
  offering: Offering | null
  onSelectPlan: (pkg: Package | null, tier: Tier, period: BillingPeriod) => void
  isProcessing?: boolean
  errorMessage?: string | null
  appliedPromo?: AppliedPromo | null
  /** Promo code accepted, but RC stripped its intro pricing for this customer. */
  promoStripped?: boolean
  promoError?: string | null
  isApplyingPromo?: boolean
  onApplyPromoCode?: (code: string) => void
  onRemovePromoCode?: () => void
  initialPromoCode?: string
}

const fadeTransition = { duration: 0.2, ease: 'easeInOut' as const }

const DarkPlanPicker: React.FC<DarkPlanPickerProps> = ({
  offering,
  onSelectPlan,
  isProcessing,
  errorMessage,
  appliedPromo,
  promoStripped,
  promoError,
  isApplyingPromo,
  onApplyPromoCode,
  onRemovePromoCode,
  initialPromoCode,
}) => {
  const [period, setPeriod] = useState<BillingPeriod>('yearly')

  const renderCard = (plan: PlanConfig, cardPeriod: BillingPeriod) => {
    const emphasized = Boolean(plan.highlighted)
    const pkg = getPackageForPlan(offering, plan.tier, cardPeriod)
    const price = formatMonthlyPrice(
      pkg,
      cardPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
      cardPeriod
    )
    const yearlyPkg = getPackageForPlan(offering, plan.tier, 'yearly')
    const yearlyTotal = formatYearlyTotal(yearlyPkg, plan.yearlyTotal)
    // The crossed-out anchor must be the LIVE monthly price (RC is the price
    // source of truth); the config string is only the offline fallback.
    const monthlyAnchorPrice = formatBaseMonthlyPrice(
      getPackageForPlan(offering, plan.tier, 'monthly'),
      plan.monthlyPrice,
      'monthly'
    )

    // Promo offerings price the FIRST period via an intro phase; the
    // card leads with the discounted number and keeps the base price
    // visible so the deal is legible (strikethrough + "then" note).
    const introApplied = hasIntroDiscount(pkg)
    const promoBasePrice = introApplied
      ? formatBaseMonthlyPrice(pkg, plan.monthlyPrice, cardPeriod)
      : null
    const postIntroNote = introApplied
      ? cardPeriod === 'monthly'
        ? `First month, then ${formatBaseMonthlyPrice(pkg, plan.monthlyPrice, 'monthly')}/mo`
        : `First year, then ${formatBaseYearlyTotal(yearlyPkg, plan.yearlyTotal)}/yr`
      : null

    return (
      <PlanCard
        plan={plan}
        price={price}
        yearlyTotal={yearlyTotal}
        period={cardPeriod}
        emphasized={emphasized}
        badge={emphasized ? 'Most popular' : undefined}
        monthlyAnchorPrice={monthlyAnchorPrice}
        promoBasePrice={promoBasePrice}
        postIntroNote={postIntroNote}
        // Null package = RC offering unavailable (config-fallback prices), not
        // "trial stripped" — keep the historical trial copy in that case.
        hasTrial={pkg ? hasFreeTrial(pkg) : true}
        promoApplied={Boolean(appliedPromo)}
        isProcessing={isProcessing}
        onSelect={() => onSelectPlan(pkg, plan.tier, cardPeriod)}
      />
    )
  }

  const promoSection = onApplyPromoCode ? (
    <PromoCodeSection
      appliedPromo={appliedPromo}
      stripped={promoStripped}
      error={promoError}
      isApplying={isApplyingPromo}
      onApply={onApplyPromoCode}
      onRemove={onRemovePromoCode}
      initialCode={initialPromoCode}
    />
  ) : null

  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col gap-6">
      <div className="flex flex-col gap-3 px-1 text-center sm:text-left">
        <Eyebrow>Choose your plan</Eyebrow>
        <h2 className="text-[32px] leading-[1.05] font-semibold tracking-[-0.02em] text-paper sm:text-[40px]">
          You're one click from your first post.
        </h2>
      </div>

      {/* A stripped promo is a do-not-buy warning; it must be seen BEFORE the
          plan cards, not discovered by scrolling to the quiet promo slot. */}
      {promoStripped && promoSection}

      <div className="mx-auto sm:mx-0 sm:self-end">
        <PeriodToggle period={period} onChange={setPeriod} />
      </div>

      {/* `pt-3` reserves room for the `-top-3` "Most popular" badge —
          without it the badge clips against the carousel's auto-overflow
          Y-clip context. */}
      <div
        className={cn(
          'no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto px-5 pt-3 pb-2',
          'sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pt-0 sm:pb-0'
        )}
      >
        {PLANS_CONFIG.map(plan => (
          <div
            key={plan.tier}
            className={cn(
              'w-[85vw] max-w-[340px] shrink-0 snap-start sm:w-auto sm:max-w-none',
              // On the mobile carousel the highlighted plan leads — it must
              // not hide off-screen as slide two.
              plan.highlighted && 'order-first sm:order-none'
            )}
          >
            {renderCard(plan, period)}
          </div>
        ))}
      </div>

      <SocialProofStrip />

      {/* Distanced from the above-the-fold section on purpose: promo holders
          scroll and look for it, everyone else shouldn't meet a discount
          prompt on first scan. */}
      {!promoStripped && promoSection && <div className="mt-6">{promoSection}</div>}

      <PaywallFAQ hasTrial={offeringHasFreeTrial(offering)} />

      {errorMessage && (
        <div role="alert" className="alert">
          {errorMessage}
        </div>
      )}

      <p className="text-center text-[11px] text-paper-faint">
        By proceeding, you agree to the{' '}
        <a
          href="https://taka.ai/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="link-muted"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://taka.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="link-muted"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  )
}

interface FaqItem {
  q: string
  a: string
}

/** Trial-dependent items and answers must match what this customer was
    actually offered — a stripped-trial customer reading "nothing today,
    charged on Day 7" would be charged in full immediately. */
const buildFaqItems = (hasTrial: boolean): FaqItem[] => [
  ...(hasTrial
    ? [
        {
          q: 'What if I cancel during the trial?',
          a: "Nothing happens, you won't be charged. Cancel from your account settings, no questions asked.",
        },
      ]
    : []),
  {
    q: 'Can I switch plans later?',
    a: 'Yes, anytime. Upgrade or downgrade from your account settings; we prorate the difference.',
  },
  {
    q: 'What if I run out of credits?',
    a: "You can buy add-on credits anytime, or upgrade your plan. We'll email you when you're running low.",
  },
  {
    q: 'What are bonus credits?',
    // Amounts derive from PLANS_CONFIG so a bonus change can't leave the FAQ
    // contradicting the perk line one scroll up.
    a: `Annual plans include a one-time bonus (${PLANS_CONFIG.map(
      p => `${p.annualBonusCredits} on ${p.name}`
    ).join(
      ', '
    )}) added when your first payment goes through. Bonus credits never expire and stack on top of your monthly allowance.`,
  },
  {
    q: 'When do I get charged?',
    a: hasTrial
      ? 'With the free trial: nothing today, charged on Day 7 unless you cancel. Monthly plans bill right away.'
      : 'Right away. Monthly plans bill monthly, yearly plans bill the full year at checkout.',
  },
  {
    q: 'Is my content private?',
    a: "Yes. Your brand kit, drafts, and posts are private to your team. We don't train AI on your content.",
  },
]

/** Single-row-open keeps the layout from accordion-stretching too tall when
    users explore multiple questions. */
const PaywallFAQ: React.FC<{ hasTrial: boolean }> = ({ hasTrial }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <div className="mt-6 flex flex-col gap-2">
      <Eyebrow>Frequently asked</Eyebrow>
      <div className="card overflow-hidden">
        {buildFaqItems(hasTrial).map((item, i) => (
          <FaqRow
            key={item.q}
            question={item.q}
            answer={item.a}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            isFirst={i === 0}
          />
        ))}
      </div>
    </div>
  )
}

const FaqRow: React.FC<{
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
  isFirst: boolean
}> = ({ question, answer, isOpen, onToggle, isFirst }) => (
  <div className={cn(!isFirst && 'border-t border-line')}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-[14px] font-medium text-paper transition-colors hover:bg-bg-3"
    >
      {question}
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0 text-paper-dim transition-transform',
          isOpen && 'rotate-180'
        )}
        strokeWidth={2}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <p className="px-5 pb-4 text-[13px] leading-[1.55] text-paper-dim">{answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)

const PeriodToggle: React.FC<{
  period: BillingPeriod
  onChange: (next: BillingPeriod) => void
}> = ({ period, onChange }) => (
  <label htmlFor="pay-yearly-switch" className="inline-flex cursor-pointer items-center gap-2.5">
    <Gift size={15} strokeWidth={2} className="shrink-0 text-acid" />
    <span className="text-[13px] font-medium text-paper">Save with yearly billing</span>
    <Switch
      id="pay-yearly-switch"
      variant="dark"
      checked={period === 'yearly'}
      onCheckedChange={checked => onChange(checked ? 'yearly' : 'monthly')}
    />
  </label>
)

const PlanCard: React.FC<{
  plan: PlanConfig
  price: string
  yearlyTotal: string
  period: BillingPeriod
  /** The highlighted plan carries the emphasis (acid border + acid CTA). */
  emphasized?: boolean
  /** Badge above the card (e.g. "Most popular"). */
  badge?: string
  /** Live monthly package price — the crossed-out anchor in the yearly view. */
  monthlyAnchorPrice: string
  /** Base per-month price to strike through when a promo intro price leads. */
  promoBasePrice?: string | null
  /** "First month, then $19/mo" — what the promo price reverts to. */
  postIntroNote?: string | null
  /** Whether RC kept the free-trial phase for this customer on this package. */
  hasTrial: boolean
  /** A promo code is applied — generic savings claims step aside either way
      (intro pricing carries the real deal, or the strip warning owns the story). */
  promoApplied?: boolean
  isProcessing?: boolean
  onSelect: () => void
}> = ({
  plan,
  price,
  yearlyTotal,
  period,
  emphasized,
  badge,
  monthlyAnchorPrice,
  promoBasePrice,
  postIntroNote,
  hasTrial,
  promoApplied,
  isProcessing,
  onSelect,
}) => {
  const isYearly = period === 'yearly'
  const showAnchor = isYearly || Boolean(promoBasePrice)
  return (
    <div
      className={cn(
        'relative flex flex-col gap-5 rounded-[12px] border p-6 transition-all',
        emphasized
          ? 'border-acid/50 bg-bg-3 shadow-[0_24px_60px_-30px_var(--acid-soft)]'
          : 'border-line bg-bg-2'
      )}
    >
      {badge && (
        <span className="text-ink-on-acid mono absolute -top-3 left-6 rounded-full bg-acid px-2.5 py-1 text-[10px] font-semibold tracking-[0.04em] uppercase">
          {badge}
        </span>
      )}

      <div className="flex flex-col gap-3">
        <h3 className="text-[18px] font-semibold tracking-[-0.01em] text-paper">{plan.name}</h3>
        <div className="flex items-baseline gap-2">
          {/* The monthly price never moves or resizes on toggle — yearly draws a
              cross through it, then the arrow + deal price slide in beside it. */}
          <span className="relative inline-block">
            <motion.span
              className="inline-block text-[40px] leading-none font-semibold tracking-[-0.02em]"
              initial={false}
              animate={{ color: showAnchor ? 'var(--paper-dim)' : 'var(--paper)' }}
              transition={fadeTransition}
            >
              {showAnchor ? (promoBasePrice ?? monthlyAnchorPrice) : price}
            </motion.span>
            <motion.span
              aria-hidden
              className="absolute top-[52%] left-[-3%] h-[3px] w-[106%] origin-left rounded-full bg-[var(--paper-dim)]"
              initial={false}
              animate={{ scaleX: showAnchor ? 1 : 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </span>
          <AnimatePresence>
            {showAnchor && (
              <motion.span
                key="deal-price"
                className="flex items-baseline gap-2"
                initial={{ opacity: 0, x: -8 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { delay: 0.18, duration: 0.25, ease: 'easeOut' },
                }}
                exit={{ opacity: 0, x: -8, transition: fadeTransition }}
              >
                <span className="text-[20px] font-semibold text-paper-dim">→</span>
                <span className="text-[40px] leading-none font-semibold tracking-[-0.02em] text-paper">
                  {price}
                </span>
              </motion.span>
            )}
          </AnimatePresence>
          <span className="text-[13px] text-paper-dim">/ mo</span>
        </div>
        {/* The annual deal block: billed total, then three DISTINCT incentives
            in one gift card — risk (trial), price (discount), quantity (bonus
            credits). Monthly intentionally renders nothing here; the whole
            block collapses so the deal reads as exclusive to yearly. */}
        <AnimatePresence initial={false}>
          {(isYearly || postIntroNote) && (
            <motion.div
              key="annual-deal"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={fadeTransition}
              className="-mt-1 overflow-hidden"
            >
              <div className="flex flex-col gap-1.5">
                {isYearly && (
                  <p className="text-[11px] text-paper-dim">{yearlyTotal} / yr billed annually</p>
                )}
                {isYearly && (
                  <div className="flex items-center gap-3 rounded-lg border border-acid/25 bg-[var(--acid-tint)] p-2.5">
                    <Gift size={32} strokeWidth={1.75} className="shrink-0 text-acid" />
                    <div className="flex flex-col gap-1">
                      {/* Only claim the trial when RC kept the trial phase for
                          this customer — past trial starts strip it. */}
                      {hasTrial && <AnnualPerk>7-day free trial</AnnualPerk>}
                      {/* Under a promo intro price the crossed price + "then"
                          note carry the real (deeper) deal — the standard 20%
                          claim would be wrong, so it steps aside. Same when a
                          code is applied but its intro was stripped: generic
                          savings copy next to an applied code reads as the
                          code working (the Whitney case). */}
                      {!promoBasePrice && !promoApplied && (
                        <AnnualPerk>20% discount (save {plan.annualSavings})</AnnualPerk>
                      )}
                      <AnnualPerk>
                        +{plan.annualBonusCredits} bonus credits (a {plan.annualBonusValue} value)
                      </AnnualPerk>
                    </div>
                  </div>
                )}
                {postIntroNote && <p className="text-[11px] text-paper-dim">{postIntroNote}</p>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ul className="flex flex-col gap-2">
        <PlanRow>
          <span className="font-medium text-paper">{plan.credits}</span> AI generation credits /
          month
        </PlanRow>
        <PlanRow>
          <span className="font-medium text-paper">{plan.seats}</span> team seat
          {plan.seats > 1 ? 's' : ''}
        </PlanRow>
        {plan.features.map(f => (
          <PlanRow key={f}>{expandFeatureCopy(f)}</PlanRow>
        ))}
      </ul>

      <Button
        variant={emphasized ? 'acid' : 'ghost-dark'}
        size="cta"
        onClick={onSelect}
        disabled={isProcessing}
        aria-busy={isProcessing}
        className="!h-[44px]"
      >
        {isProcessing
          ? 'Processing…'
          : isYearly && hasTrial
            ? 'Start my free trial'
            : `Get ${plan.name}`}
      </Button>

      <CardSafetyNet period={period} hasTrial={hasTrial} />
    </div>
  )
}

/** Yearly with a live trial phase carries the trial-specific safety net;
    monthly and trial-stripped customers bill now, so the trial language drops. */
const CardSafetyNet: React.FC<{ period: BillingPeriod; hasTrial: boolean }> = ({
  period,
  hasTrial,
}) => {
  const items =
    period === 'yearly' && hasTrial
      ? ['No charge today', `Day ${TRIAL_REMINDER_DAY} reminder`, 'Cancel anytime']
      : ['Cancel anytime']
  // Render each item + separator as discrete spans inside a wrapping flex
  // so line breaks always fall between items, not on the `·` separators
  // (which would leave orphan dots when the line wraps on narrow viewports).
  return (
    <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-paper-dim">
      {items.map((item, i) => (
        <Fragment key={item}>
          {i > 0 && <span aria-hidden>·</span>}
          <span>{item}</span>
        </Fragment>
      ))}
    </p>
  )
}

const PlanRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="text-[13px] text-paper-dim">{children}</li>
)

/** One line of the annual deal block — acid tick, paper text. */
const AnnualPerk: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11.5px] font-medium text-paper">{children}</p>
)

/**
 * Trust anchor at the money moment: a marquee of REAL paying Taka businesses
 * (curated from prod — active subscription + a logo that reads at 20px).
 * Assets are committed thumbnails under /social-proof, not live attachment
 * URLs, so the strip never breaks when a signed URL expires.
 */
/** Real paying customers, curated from prod 2026-07-18 (criterion: active
    subscription + a logo that reads at 36px; source: BrandKits.logoId →
    Attachments, hand-picked from contact sheets of all 177 paying-business
    logos; 6 legacy strip members kept by explicit call while their
    subscriptions wind down). Static by design — refresh the set every few
    months and drop any business that churns or asks out. */
const SOCIAL_PROOF_BUSINESSES: Array<{ name: string; logo: string }> = [
  { name: 'His House', logo: 'his-house' },
  { name: 'First Primary Care', logo: 'first-primary-care' },
  { name: 'Cafely', logo: 'cafely' },
  { name: 'AB Garage Door', logo: 'ab-garage-door' },
  { name: 'MÜNE Aesthetics', logo: 'mune-aesthetics' },
  { name: 'Globster', logo: 'globster' },
  { name: "Bruno's Bounce House", logo: 'brunos-bounce-house' },
  { name: 'Multi-Prêts', logo: 'multi-prets' },
  { name: "NayNay's Nails", logo: 'naynay-s-nails' },
  { name: 'BVM Custom Homes', logo: 'bvm-custom-homes' },
  { name: 'Supermind', logo: 'supermind' },
  { name: 'Ross Real Estate', logo: 'ross-real-estate-services' },
  { name: 'Beyond Fitness', logo: 'beyond-fitness' },
  { name: 'Ruislip Manor Pharmacy', logo: 'ruislip-manor-pharmacy' },
  { name: 'Skunkyrunky', logo: 'skunkyrunky' },
  { name: 'Harmony.ai', logo: 'harmony-ai' },
  { name: 'The Hula Hut', logo: 'the-hula-hut' },
  { name: 'Tacoma Chem-Dry', logo: 'tacoma-chem-dry' },
  { name: 'Nova Clave Music', logo: 'nova-clave-music' },
  { name: 'Xclusive Homes', logo: 'xclusive-homes' },
  { name: 'Cotswold Trees', logo: 'cotswold-trees' },
  { name: 'MedSync Corp', logo: 'medsync-corp' },
  { name: 'Salt City Stays', logo: 'salt-city-stays' },
  { name: 'CrossFit Bux', logo: 'crossfit-bux' },
  { name: 'Bigolux', logo: 'bigolux' },
  { name: 'HomeCura', logo: 'homecura' },
  { name: 'Pebre & Co.', logo: 'pebre-co' },
  { name: 'Face Aesthetic Artistry', logo: 'face-aesthetic-artistry' },
  { name: 'Fieldz AI', logo: 'fieldz-ai' },
  { name: 'Trails at 620', logo: 'trails-at-620' },
  { name: 'ReadySetTow', logo: 'readysettow' },
  { name: 'Altura Recovery', logo: 'altura-recovery' },
  { name: 'Gallery 334', logo: 'gallery-334' },
  { name: 'Vamos Padel', logo: 'vamos-padel' },
  { name: 'Frontier Kitchen', logo: 'frontier-kitchen' },
  { name: 'Monday Morning', logo: 'monday-morning' },
  { name: 'Sunshine & Saline', logo: 'sunshine-saline' },
  { name: 'Black United Clothing', logo: 'black-united-clothing' },
  { name: 'Timeless Retreats', logo: 'timeless-retreats' },
  { name: 'RankOps', logo: 'rankops' },
  { name: 'NUYU', logo: 'nuyu' },
  { name: 'British Craft Studio', logo: 'british-craft-studio' },
  { name: 'Elevate Fitness', logo: 'elevate-fitness-leander' },
  { name: 'Maison Noire Américaine', logo: 'maison-noire' },
  { name: 'ReShape Rx', logo: 'reshape-rx' },
  { name: 'Apex Society Club', logo: 'apex-society-club' },
  { name: 'Wild Haggis Protein', logo: 'wild-haggis-protein' },
  { name: 'WrenchSpeak', logo: 'wrenchspeak' },
  { name: 'BizzyBee', logo: 'bizzybee' },
  { name: 'Ocean Digital Arts', logo: 'ocean-digital-arts' },
]

/** One full copy of the logo chips. The marquee renders it twice and slides
    the track by exactly -50%, so each half must be self-contained (own flex
    gap + trailing pr-3 standing in for the inter-copy gap) — otherwise the
    -50% point lands mid-gap and the loop visibly snaps at each wrap. */
const MarqueeHalf: React.FC<{ ariaHidden?: boolean }> = ({ ariaHidden }) => (
  <div className="flex items-center gap-3 pr-3" aria-hidden={ariaHidden}>
    {SOCIAL_PROOF_BUSINESSES.map(biz => (
      <span
        key={biz.logo}
        className="inline-flex shrink-0 items-center gap-2.5 rounded-full border border-line bg-bg-2 py-1.5 pr-4 pl-1.5 text-[13px] font-medium whitespace-nowrap text-paper"
      >
        {/* Light disc behind each mark — most SMB logos are drawn for
            light backgrounds and vanish on the dark chip without it. */}
        <img
          src={`/social-proof/${biz.logo}.webp`}
          alt=""
          width={36}
          height={36}
          draggable={false}
          className="h-9 w-9 rounded-full bg-white/90 object-contain p-0.5"
        />
        {biz.name}
      </span>
    ))}
  </div>
)

// memo: fully static, but sits under the period-toggle state — without memo
// every yearly/monthly flip re-reconciles ~150 vnodes mid-animation.
const SocialProofStrip = memo(() => (
  <div className="flex flex-col items-center gap-2.5">
    <style>{`
      @keyframes paywall-marquee {
        from { transform: translateX(0); }
        to { transform: translateX(-50%); }
      }
      @media (prefers-reduced-motion: reduce) {
        .paywall-marquee-track { animation: none !important; }
      }
    `}</style>
    <p className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
      Join <span className="text-paper">500+</span> businesses posting with Taka
    </p>
    <div
      className="w-full overflow-hidden"
      style={{
        maskImage: 'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
      }}
    >
      <div
        className="paywall-marquee-track pointer-events-none flex w-max items-center select-none"
        style={{ animation: 'paywall-marquee 190s linear infinite' }}
      >
        <MarqueeHalf />
        <MarqueeHalf ariaHidden />
      </div>
    </div>
  </div>
))
SocialProofStrip.displayName = 'SocialProofStrip'

/** Bolds the leading "All"/"Unlimited" so those rows match the numeric-bold
    rhythm of the credits/seats rows. */
const expandFeatureCopy = (f: string): React.ReactNode => {
  const boldLead = ['All ', 'Unlimited '].find(lead => f.startsWith(lead))
  if (boldLead) {
    const word = boldLead.trim()
    return (
      <>
        <span className="font-medium text-paper">{word}</span>
        {f.slice(word.length)}
      </>
    )
  }
  return f
}

// ─── promo code ─────────────────────────────────────────────────────────────

const PROMO_ERROR_COPY: Record<string, string> = {
  invalid_format: "That code doesn't look right. Double-check and try again.",
  not_found: "That code doesn't look right. Double-check and try again.",
  disabled: 'This code is no longer active.',
  expired: 'This code has expired.',
  exhausted: 'This code has hit its redemption limit.',
  already_redeemed: "You've already used this code.",
  offering_not_found: "This code couldn't be applied. Contact support.",
  no_business: 'Finish setting up your business first, then try the code again.',
  grant_failed: "Couldn't apply the code right now. Try again in a moment.",
  network: 'Something went wrong. Try again.',
}

function getPromoErrorReason(error: unknown): string {
  const body = (error as { body?: { reason?: string } })?.body
  if (body?.reason && body.reason in PROMO_ERROR_COPY) return body.reason
  if (error === 'offering_not_found') return 'offering_not_found'
  return 'network'
}

interface PromoCodeSectionProps {
  appliedPromo?: AppliedPromo | null
  /** Code accepted, but RC stripped its discounted pricing for this customer. */
  stripped?: boolean
  error?: string | null
  isApplying?: boolean
  onApply: (code: string) => void
  onRemove?: () => void
  initialCode?: string
}

/**
 * Deliberately quiet: normal users see one low-contrast text link below the
 * plan cards. Only holders of a code (or a ?promo= link) ever interact with it.
 */
const PromoCodeSection: React.FC<PromoCodeSectionProps> = ({
  appliedPromo,
  stripped,
  error,
  isApplying,
  onApply,
  onRemove,
  initialCode,
}) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(initialCode) || Boolean(error))
  const [code, setCode] = useState(initialCode ?? '')

  useEffect(() => {
    if (error) setIsExpanded(true)
  }, [error])

  if (appliedPromo && stripped) {
    // The code exists and was accepted, but RevenueCat refused its discounted
    // pricing for THIS customer, so a purchase now would charge full price.
    // Say exactly that instead of celebrating the code (the Whitney case:
    // "applied" banner + full price + generic savings copy read as a $90 deal
    // that would have billed $180).
    return (
      <div
        data-testid="promo-stripped-banner"
        role="alert"
        className="flex items-start gap-2.5 rounded-[10px] border border-amber-400/40 bg-amber-400/10 px-4 py-3"
      >
        <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" strokeWidth={2} />
        <div className="flex flex-1 flex-col gap-1.5">
          <p className="text-[13px] text-paper">
            Code <span className="mono font-semibold text-amber-400">{appliedPromo.code}</span> is
            valid, but its discounted pricing isn't loading for your account, so checkout would
            charge the full plan price. Hold off on purchasing. Message us and we'll set the
            discount up for you.
          </p>
          <button
            type="button"
            data-testid="promo-stripped-support"
            onClick={() => {
              // intercom.show() is a silent no-op pre-boot (ad-blockers, boot
              // failure) — this button is the user's only escape hatch, so
              // fall back to email rather than doing nothing.
              if (intercom.isInitialized()) {
                intercom.show()
              } else {
                window.location.href = 'mailto:support@taka.ai'
              }
            }}
            className="w-fit text-[12px] font-medium text-paper underline decoration-dotted underline-offset-4 transition-colors hover:text-amber-300"
          >
            Message support
          </button>
        </div>
        {onRemove && (
          <button
            type="button"
            data-testid="promo-code-remove"
            onClick={onRemove}
            className="shrink-0 text-[12px] text-paper-faint underline decoration-dotted underline-offset-4 transition-colors hover:text-paper-dim"
          >
            Remove
          </button>
        )}
      </div>
    )
  }

  if (appliedPromo) {
    return (
      <div
        data-testid="promo-applied-banner"
        className="flex items-center gap-2.5 rounded-[10px] border border-acid/40 bg-acid/10 px-4 py-3"
      >
        <Ticket className="h-4 w-4 shrink-0 text-acid" strokeWidth={2} />
        <p className="flex-1 text-[13px] text-paper">
          Code <span className="mono font-semibold text-acid">{appliedPromo.code}</span> applied.
          {appliedPromo.campaign
            ? ` Partner pricing from ${appliedPromo.campaign} unlocked.`
            : ' Partner pricing unlocked.'}
        </p>
        {onRemove && (
          <button
            type="button"
            data-testid="promo-code-remove"
            onClick={onRemove}
            className="shrink-0 text-[12px] text-paper-faint underline decoration-dotted underline-offset-4 transition-colors hover:text-paper-dim"
          >
            Remove
          </button>
        )}
      </div>
    )
  }

  if (!isExpanded) {
    return (
      <div className="flex justify-center">
        <button
          type="button"
          data-testid="promo-code-link"
          onClick={() => setIsExpanded(true)}
          className="text-[12px] text-paper-faint underline decoration-dotted underline-offset-4 transition-colors hover:text-paper-dim"
        >
          Have a promo code?
        </button>
      </div>
    )
  }

  const handleSubmit = () => {
    if (!code.trim() || isApplying) return
    onApply(code)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="flex flex-col items-center gap-2"
    >
      <div className="flex w-full max-w-[420px] items-center gap-2">
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Promo code"
          autoFocus
          data-testid="promo-code-input"
          className="mono h-[40px] flex-1 rounded-[10px] border border-line bg-bg-2 px-3.5 text-[13px] tracking-[0.04em] text-paper uppercase transition-colors outline-none placeholder:text-paper-faint focus:border-acid/60"
        />
        <Button
          variant="ghost-dark"
          size="cta"
          onClick={handleSubmit}
          disabled={isApplying || !code.trim()}
          aria-busy={isApplying}
          data-testid="promo-code-apply"
          className="!h-[40px] !w-auto shrink-0 px-4"
        >
          {isApplying ? 'Applying…' : 'Apply'}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-[12px] text-red-400">
          {error}
        </p>
      )}
    </motion.div>
  )
}

// ─── helpers ────────────────────────────────────────────────────────────────

async function checkTesterGrant(productVendorId: string): Promise<boolean> {
  try {
    const result = await BillingService.billingControllerCheckTesterGrantV1({ productVendorId })
    return result.granted === true
  } catch {
    return false
  }
}

const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
